/**
 * Configuration centralisée de l'API Netikash
 * Source unique de vérité pour tous les endpoints, URLs et headers
 */

// Base URLs
export const BASE_URLS = {
    accounts: {
        production: 'https://accounts.netikash.com',
        sandbox: 'https://accounts.netikash.com' // À ajuster si différent
    },
    api: {
        production: 'https://api.netikash.com/api/v1/trs',
        sandbox: 'https://api.netikash.com/api/v1/trs' // À ajuster si différent
    },
    // Endpoint exposé pour le flux B (payments) — utilise sender_id du body ; nécessaire pour sender_id = "anonymous"
    apiExposed: {
        production: 'https://api.netikash.com/exposed/v1/trs',
        sandbox: 'https://api.netikash.com/exposed/v1/trs'
    }
};

// Version API actuelle
export const API_VERSION = 'v1';

// Endpoints avec méthode, path et description
export const ENDPOINTS = {
    auth: {
        method: 'POST',
        path: '/oauth2/token',
        description: 'Obtenir un token d\'accès',
        baseUrl: (env = 'production') => BASE_URLS.accounts[env],
        fullUrl: (env = 'production') => `${BASE_URLS.accounts[env]}/oauth2/token`
    },
    payment: {
        method: 'POST',
        path: '/transactions/requests/payments',
        description: 'Créer une demande de paiement',
        baseUrl: (env = 'production') => BASE_URLS.api[env],
        fullUrl: (env = 'production') => `${BASE_URLS.api[env]}/transactions/requests/payments`
    },
    paymentRequestById: {
        method: 'GET',
        path: '/transactions/requests/{id}',
        description: 'Récupérer le détail et le statut d\'une demande de paiement par son identifiant',
        baseUrl: (env = 'production') => BASE_URLS.api[env],
        fullUrl: (id, env = 'production') => `${BASE_URLS.api[env]}/transactions/requests/${encodeURIComponent(id)}`
    },
    cliPayment: {
        method: 'POST',
        path: '/transactions/requests/cli-payments',
        description: 'Créer une demande de paiement avec lien de redirection (flux client)',
        baseUrl: (env = 'production') => BASE_URLS.api[env],
        fullUrl: (env = 'production') => `${BASE_URLS.api[env]}/transactions/requests/cli-payments`
    },
    callbackDestinations: {
        method: 'GET',
        path: '/callback-destinations',
        description: 'Liste des destinations de callback (webhooks)',
        baseUrl: (env = 'production') => BASE_URLS.api[env],
        fullUrl: (env = 'production') => `${BASE_URLS.api[env]}/callback-destinations`
    },
    callbackDestinationById: {
        method: 'GET',
        path: '/callback-destinations/{id}',
        description: 'Détail d\'une destination de callback'
    },
    callbackDestinationCreate: {
        method: 'POST',
        path: '/callback-destinations',
        description: 'Créer une destination de callback (URL webhook + clé de signature)'
    },
    callbackDestinationUpdate: {
        method: 'PUT',
        path: '/callback-destinations/{id}',
        description: 'Modifier une destination de callback'
    },
    callbackDestinationDelete: {
        method: 'DELETE',
        path: '/callback-destinations/{id}',
        description: 'Supprimer une destination de callback'
    }
};

// Headers standards par endpoint
export const STANDARD_HEADERS = {
    auth: [
        { name: 'Content-Type', value: 'application/json', required: true, description: 'Type de contenu' },
        { name: 'Accept', value: 'application/json', required: false, description: 'Type de réponse attendu' },
        { name: 'Authorization', value: 'Basic base64(client_id:client_secret)', required: true, description: 'Credentials encodés en Base64' }
    ],
    payment: [
        { name: 'Content-Type', value: 'application/json', required: true, description: 'Type de contenu' },
        { name: 'Accept', value: 'application/json', required: true, description: 'Type de réponse attendu' },
        { name: 'Authorization', value: 'Bearer <access_token>', required: true, description: 'Token OAuth2 obtenu via /oauth2/token' }
    ],
    paymentRequestById: [
        { name: 'Content-Type', value: 'application/json', required: true, description: 'Type de contenu' },
        { name: 'Accept', value: 'application/json', required: true, description: 'Type de réponse attendu' },
        { name: 'Authorization', value: 'Bearer <access_token>', required: true, description: 'Token OAuth2 obtenu via /oauth2/token' }
    ],
    webhook: [
        { name: 'X-Signature', value: 'string', required: true, description: 'Signature du payload' },
        { name: 'X-Timestamp', value: 'string', required: true, description: 'Timestamp de signature' }
    ]
};

// Helper functions pour construire les URLs complètes
export const getAuthUrl = (env = 'production') => {
    return `${BASE_URLS.accounts[env]}${ENDPOINTS.auth.path}`;
};

export const getPaymentUrl = (env = 'production') => {
    return `${BASE_URLS.api[env]}${ENDPOINTS.payment.path}`;
};

/** URL pour le flux B (payments) — endpoint exposé. À utiliser quand sender_id = "anonymous" (payeur sans compte Netikash). */
export const getPaymentExposedUrl = (env = 'production') => {
    return `${BASE_URLS.apiExposed[env]}${ENDPOINTS.payment.path}`;
};

export const getPaymentRequestByIdUrl = (id, env = 'production') => {
    return `${BASE_URLS.api[env]}/transactions/requests/${encodeURIComponent(id)}`;
};

export const getCliPaymentUrl = (env = 'production') => {
    return `${BASE_URLS.api[env]}${ENDPOINTS.cliPayment.path}`;
};

// --- Service Comptes (OAuth2) : obtenir solution_id, receiver_id, sender_id, service ---
export const ACCOUNTS_PATHS = {
    solutions: '/exposed/v1/usr/solutions',
    solutionById: (id) => `/exposed/v1/usr/solutions/${id}`,
    solutionByCode: (code) => `/exposed/v1/usr/solutions?code=${encodeURIComponent(code)}`,
    companies: (ids) => `/exposed/v1/usr/companies?ids=${encodeURIComponent(ids)}`,
    companyById: (id) => `/exposed/v1/usr/companies/${id}`,
    companyShowById: (id) => `/exposed/v1/usr/companies/show/${id}`,
    companyIsOnSolution: (orgId, solId) => `/exposed/v1/usr/companies/${orgId}/is-on-solution/${solId}`,
    servicesByOrgSol: (orgId, solId) => `/exposed/v1/usr/services?org=${encodeURIComponent(orgId)}&sol=${encodeURIComponent(solId)}`,
    serviceById: (id) => `/exposed/v1/usr/services/${id}`,
    userShow: (userId) => `/exposed/v1/usr/users/${userId}/show`,
    usersByIds: (ids) => `/exposed/v1/usr/users?ids=${encodeURIComponent(ids)}`
};

export const getAccountsBaseUrl = (env = 'production') => BASE_URLS.accounts[env];

export const getSolutionsUrl = (env = 'production') =>
    `${BASE_URLS.accounts[env]}${ACCOUNTS_PATHS.solutions}`;
export const getSolutionByIdUrl = (id, env = 'production') =>
    `${BASE_URLS.accounts[env]}${ACCOUNTS_PATHS.solutionById(id)}`;
export const getCompanyByIdUrl = (id, env = 'production') =>
    `${BASE_URLS.accounts[env]}${ACCOUNTS_PATHS.companyById(id)}`;
export const getCompanyIsOnSolutionUrl = (orgId, solId, env = 'production') =>
    `${BASE_URLS.accounts[env]}${ACCOUNTS_PATHS.companyIsOnSolution(orgId, solId)}`;
export const getServiceByOrgSolUrl = (orgId, solId, env = 'production') =>
    `${BASE_URLS.accounts[env]}${ACCOUNTS_PATHS.servicesByOrgSol(orgId, solId)}`;
export const getUserShowUrl = (userId, env = 'production') =>
    `${BASE_URLS.accounts[env]}${ACCOUNTS_PATHS.userShow(userId)}`;

// Statut des services (pour la page Status)
export const SERVICE_STATUS = {
    accounts: {
        status: 'operational',
        lastChecked: new Date().toISOString()
    },
    api: {
        status: 'operational',
        lastChecked: new Date().toISOString()
    }
};
